<!--
  Own top-level tab (Campaigns / Tasks -> a campaign -> "Slot booking"),
  sibling to Settings rather than a section buried inside it -- Ramu's own
  feedback (2026-09-04): "Configuring slots can be a separate tab itself...
  Why in one UI you are doing everything." Reads capture_datetime_field
  straight off the campaign prop (Settings tab owns setting it) rather than
  needing any cross-tab form-state coupling.
-->
<template>
  <div>
    <header class="tw-reports-pane__head">
      <div>
        <p class="tw-reports-pane__eyebrow">Slot booking</p>
        <h2 class="tw-reports-pane__title">{{ campaign?.name || 'Slot booking' }}</h2>
        <p class="tw-reports-pane__sub">
          Check real availability across named resources (a doctor, a room, a staff member)
          and book into one, instead of accepting whatever time the caller states.
        </p>
      </div>
      <button
        type="button"
        class="tw-btn tw-btn--primary"
        style="margin-left: auto; flex-shrink: 0"
        :disabled="disabled || saving"
        @click="save"
      >
        <v-icon icon="mdi-content-save-outline" size="16" />
        {{ saving ? 'Saving…' : 'Save settings' }}
      </button>
    </header>

    <div class="tw-slot-body">
      <section class="tw-slot-section">
        <div class="tw-slot-narrow">
          <v-switch
            :model-value="form.slot_booking_enabled"
            label="Check real availability and book a slot"
            color="primary"
            density="comfortable"
            hide-details
            :disabled="disabled"
            @update:model-value="(v) => (form.slot_booking_enabled = v)"
          />
          <p v-if="form.slot_booking_enabled && !captureDatetimeField" class="tw-field-hint tw-field-hint--warning">
            You can configure resources and hours below right now, but a booked slot needs
            somewhere to be written -- go to Settings -> Capture caller details and mark one
            field "This is the agreed time" before saving.
          </p>
          <p v-else class="tw-field-hint">
            When off, the AI still asks about a time and saves whatever the caller says (Capture
            caller details, in Settings) with no availability check. When on, it checks the
            resources below and only offers real open slots.
          </p>
        </div>
      </section>

      <section v-if="form.slot_booking_enabled" class="tw-slot-section">
        <div class="tw-slot-narrow">
          <span class="tw-slot-section__title">Preferred time field</span>
          <p class="tw-field-hint">
            Pick which of this campaign's already-captured fields (Settings -> Capture caller
            details) a booked slot gets written into.
          </p>
          <p v-if="!capturedFieldOptions.length" class="tw-field-hint tw-field-hint--warning">
            Nothing captured yet -- add at least one field in Settings -> Capture caller details
            first, then come back here to pick it.
          </p>
          <template v-else>
            <label>
              <span class="tw-field-label">Save the agreed time to</span>
              <select v-model="form.capture_datetime_field" class="tw-select" :disabled="disabled">
                <option value="">Choose a field…</option>
                <option v-for="opt in capturedFieldOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
              </select>
            </label>
            <label v-if="form.capture_datetime_field" style="display: block; margin-top: 10px">
              <span class="tw-field-label">What this time means</span>
              <input
                v-model="form.capture_datetime_prompt"
                type="text"
                class="tw-input"
                placeholder="e.g. a doctor's appointment"
                maxlength="300"
                :disabled="disabled"
              />
            </label>
            <label v-if="form.capture_datetime_field" style="display: block; margin-top: 10px">
              <span class="tw-field-label">Also save the time to</span>
              <select v-model="form.capture_datetime_time_field" class="tw-select" :disabled="disabled">
                <option value="">Keep the full date and time together above</option>
                <option v-for="opt in capturedFieldOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
              </select>
              <p class="tw-field-hint">
                Optional. Leave unset to save the whole agreed moment in the field above. Pick a
                field here to split it instead -- the field above gets just the date, this one
                gets just the time.
              </p>
            </label>
          </template>
        </div>
      </section>

      <section v-if="form.slot_booking_enabled" class="tw-slot-section">
        <div class="tw-slot-resources__head">
          <span class="tw-slot-section__title">Resources</span>
          <v-btn
            v-if="activeResourceForm !== 'new'"
            size="small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-plus"
            :disabled="disabled"
            @click="openAddResource"
          >
            Add resource
          </v-btn>
        </div>
        <p class="tw-field-hint">
          Each resource (a doctor, a room, a staff member) has its own hours and capacity. A
          caller who doesn't name one is offered whichever active resource is open.
        </p>

        <SlotResourceForm
          v-if="activeResourceForm === 'new'"
          :task-id="taskId"
          :resource="null"
          @saved="onResourceSaved"
          @cancel="closeResourceForm"
          @error="onResourceError"
        />

        <div v-if="slotResourcesLoading" class="tw-adv-processing" style="margin-top: 10px">
          <v-progress-circular indeterminate color="primary" size="18" width="2" />
          <span>Loading resources…</span>
        </div>
        <p v-else-if="!slotResources.length && activeResourceForm !== 'new'" class="tw-field-hint" style="margin-top: 10px">
          No resources yet -- add at least one before this campaign can actually book anything.
        </p>
        <div v-else class="tw-slot-resources__list">
          <div
            v-for="resource in slotResources"
            :key="resource.resource_id"
            :class="{ 'tw-slot-resources__item--editing': isEditingResource(resource) }"
          >
            <SlotResourceForm
              v-if="isEditingResource(resource)"
              :task-id="taskId"
              :resource="resource"
              @saved="onResourceSaved"
              @cancel="closeResourceForm"
              @error="onResourceError"
            />
            <div v-else class="tw-slot-resources__row">
              <div class="tw-slot-resources__row-top">
                <div class="tw-slot-resources__avatar">
                  <v-icon icon="mdi-account-clock-outline" size="18" />
                </div>
                <div class="tw-slot-resources__row-main">
                  <div class="tw-slot-resources__name-line">
                    <span class="tw-slot-resources__name">{{ resource.resource_name }}</span>
                    <span v-if="resource.is_active === false" class="tw-badge tw-badge--muted">Inactive</span>
                  </div>
                  <span class="tw-slot-resources__meta">
                    {{ resource.slot_duration_minutes }} min slots · up to {{ resource.max_concurrent }} at once
                  </span>
                </div>
              </div>
              <p class="tw-slot-resources__hours">{{ resourceHoursSummary(resource) }}</p>
              <div class="tw-slot-resources__actions">
                <v-btn size="x-small" variant="text" :disabled="disabled" @click="openEditResource(resource)">Edit</v-btn>
                <v-btn
                  size="x-small"
                  variant="text"
                  color="error"
                  :disabled="disabled"
                  @click="pendingDeleteId = resource.resource_id"
                >
                  Remove
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SlotConfirmDialog
        :model-value="!!pendingDeleteId"
        icon="mdi-trash-can-outline"
        title="Remove resource"
        subtitle="Existing bookings are kept."
        :message="pendingDeleteMessage"
        detail="No new bookings can be made against it once removed, but bookings already made are kept exactly as they are."
        confirm-label="Remove resource"
        danger
        :loading="!!deletingResourceId"
        @update:model-value="(v) => { if (!v) pendingDeleteId = '' }"
        @confirm="pendingDeleteResource && confirmDeleteResource(pendingDeleteResource)"
      />

      <!--
        Deliberately NOT gated on form.slot_booking_enabled -- this is a
        general "send the caller a WhatsApp/email" setting for the task,
        same on/off + static template shape either way (Ramu, 2026-09-04).
        When slot booking is on, it fires as the booking confirmation
        (date/time filled in) right after a successful booking. When it's
        off, the exact same toggle+template fires once as a plain
        end-of-call message instead -- see recordAICallActivity server-side.
      -->
      <section class="tw-slot-section">
        <div class="tw-slot-narrow">
          <span class="tw-slot-section__title">Caller confirmation message</span>
          <p class="tw-field-hint">
            {{ form.slot_booking_enabled
              ? 'Sent right after a successful booking, with the date/time filled in.'
              : 'Sent once at the end of every call on this campaign.' }}
          </p>
          <div class="tw-slot-notify-channel">
            <span>Email</span>
            <v-switch
              :model-value="form.slot_booking_notify_email_enabled"
              color="primary"
              density="compact"
              hide-details
              :disabled="disabled"
              @update:model-value="(v) => (form.slot_booking_notify_email_enabled = v)"
            />
          </div>
          <div class="tw-slot-notify-channel">
            <span>WhatsApp</span>
            <v-switch
              :model-value="form.slot_booking_notify_whatsapp_enabled"
              color="primary"
              density="compact"
              hide-details
              :disabled="disabled"
              @update:model-value="(v) => (form.slot_booking_notify_whatsapp_enabled = v)"
            />
          </div>
          <p class="tw-field-hint">
            WhatsApp needs an approved message template -- ask Yandle to submit one before
            turning this on.
          </p>

          <label v-if="form.slot_booking_notify_email_enabled || form.slot_booking_notify_whatsapp_enabled" style="display: block; margin-top: 10px">
            <span class="tw-field-label">Message</span>
            <VariableInsertTextarea
              v-model="form.slot_booking_confirmation_message"
              :variables="confirmationMessageVariables"
              :rows="2"
              :placeholder="form.slot_booking_enabled
                ? 'e.g. Hi {caller_name}, your appointment is confirmed. We\'ll see you then!'
                : 'e.g. Thanks for calling {org_name}. We\'ll get back to you shortly.'"
              maxlength="500"
              :disabled="disabled"
            />
            <p class="tw-field-hint">Optional -- leave blank to use Yandle's default line.</p>
          </label>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import SlotResourceForm from '@/components/Campaigns/SlotResourceForm.vue'
import SlotConfirmDialog from '@/components/Campaigns/SlotConfirmDialog.vue'
import VariableInsertTextarea from '@/components/Shared/VariableInsertTextarea.vue'
import { editCampaign, fetchSlotResources, deleteSlotResource } from '@/api/campaignsApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'CampaignSlotBooking' })

const props = defineProps({
  campaign: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['saved', 'error'])

const taskId = computed(() => String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim())
// Reads the LOCAL form field, not the prop directly -- this is now edited
// on this tab (see form.capture_datetime_field below), so it has to
// reflect an unsaved pick immediately, not just whatever was last saved.
const captureDatetimeField = computed(() => String(form.value.capture_datetime_field ?? '').trim())
// What this campaign already captures (Settings -> Capture caller
// details) -- the pool this tab picks the agreed-time field FROM, so
// there's nothing booking-specific to configure over there anymore.
const capturedFieldOptions = computed(() =>
  (Array.isArray(props.campaign?.captureLeadFields) ? props.campaign.captureLeadFields : [])
    .filter((f) => f?.field)
    .map((f) => ({ key: f.field, label: f.description || f.field })),
)

// The tokens sendCallerConfirmationMessage (backend) actually resolves for
// this message -- resource_name/date_label/time_label only apply once a
// real slot is booked, so they're only offered/previewed when slot
// booking itself is on; a plain end-of-call message never has a slot to
// describe. Also offers every field this task's own captureLeadFieldsTool
// captures (Settings -> Capture caller details) -- these get merged into
// the backend's response_data the same way resource_name/date_label/
// time_label do, so a task that captures e.g. "reason_for_visit" can
// reference {reason_for_visit} here too, not just the fixed slot-booking
// set. Never a hardcoded shape -- whatever fields THIS task configured.
const confirmationMessageVariables = computed(() => {
  const base = [
    { token: 'caller_name', label: 'Caller name', example: 'Priya Sharma' },
    { token: 'org_name', label: 'Org name', example: props.campaign?.orgName || 'Aster CMI' },
  ]
  const slotTokens = form.value.slot_booking_enabled
    ? [
        { token: 'resource_name', label: 'Resource (doctor/room/staff)', example: 'Dr. Vikram Shetty' },
        { token: 'date_label', label: 'Appointment date', example: '07/09/2026' },
        { token: 'time_label', label: 'Appointment time', example: '10:00' },
      ]
    : []
  const leadFieldTokens = (Array.isArray(props.campaign?.captureLeadFields) ? props.campaign.captureLeadFields : [])
    .filter((f) => f?.field)
    // description is the clean admin-facing label for this field (set in
    // Settings -> Capture caller details); prompt is what the AI says, not
    // fit for a picker label -- falls back to the field's own name for an
    // older row saved before description existed.
    .map((f) => ({ token: f.field, label: f.description || f.field, example: `(${f.field})` }))
  return [...base, ...slotTokens, ...leadFieldTokens]
})

function defaultForm() {
  return {
    slot_booking_enabled: false,
    slot_booking_notify_whatsapp_enabled: false,
    slot_booking_notify_email_enabled: false,
    slot_booking_confirmation_message: '',
    capture_datetime_field: '',
    capture_datetime_time_field: '',
    capture_datetime_prompt: '',
  }
}
const form = ref(defaultForm())
const saving = ref(false)

function hydrate(campaign) {
  form.value = {
    slot_booking_enabled: Boolean(campaign?.slotBookingEnabled),
    slot_booking_notify_whatsapp_enabled: Boolean(campaign?.slotBookingNotifyWhatsappEnabled),
    slot_booking_notify_email_enabled: Boolean(campaign?.slotBookingNotifyEmailEnabled),
    slot_booking_confirmation_message: String(campaign?.slotBookingConfirmationMessage ?? '').trim(),
    // 2026-09-07: owned here now, not Settings -- Ramu's own correction was
    // that picking the agreed-time field belongs on the tab that actually
    // uses it, as a selector over the fields already captured, not a
    // special flag mixed into the generic capture list itself.
    capture_datetime_field: String(campaign?.captureDatetimeField ?? '').trim(),
    capture_datetime_time_field: String(campaign?.captureDatetimeTimeField ?? '').trim(),
    capture_datetime_prompt: String(campaign?.captureDatetimePrompt ?? '').trim(),
  }
}

// Declared here, ahead of the watcher below, deliberately -- `const` isn't
// hoisted the way `function loadSlotResources` is, and the watcher's
// `immediate: true` runs synchronously during setup, so referencing these
// only after this point (they used to sit down in the "Resources" section)
// threw "Cannot access 'slotResources' before initialization" on every
// mount (confirmed live 2026-09-04).
const slotResources = ref([])
const slotResourcesLoading = ref(false)
// null = no form open; 'new' = the add-resource panel; a resource object =
// that row's inline edit panel is open. Rendered inline in the page (no
// dialog) per Ramu's explicit feedback (2026-09-04): "everything in one
// single navigation."
const activeResourceForm = ref(null)
const deletingResourceId = ref('')
// resource_id showing its SlotConfirmDialog -- was a native window.confirm(),
// then briefly an inline-expanding strip, before landing here. Ramu, 2026-09-04:
// "why this system dialogue here, man?" (the native popup) and then "you can
// give dialogue warnings, right?... why aren't you giving the dialogue"
// (asking for a proper styled one instead of the inline strip).
const pendingDeleteId = ref('')
const pendingDeleteResource = computed(() => slotResources.value.find((r) => r.resource_id === pendingDeleteId.value) || null)
const pendingDeleteMessage = computed(() => `Remove "${pendingDeleteResource.value?.resource_name || 'this resource'}"?`)

watch(
  () => props.campaign,
  (campaign) => { hydrate(campaign); void loadSlotResources() },
  { immediate: true },
)

async function save() {
  if (!taskId.value) return
  if (form.value.slot_booking_enabled && !captureDatetimeField.value) {
    emit('error', 'Pick which field captures the preferred time (above) before saving.')
    return
  }
  saving.value = true
  try {
    const response = await editCampaign({
      task_id: taskId.value,
      slot_booking_enabled: form.value.slot_booking_enabled,
      slot_booking_notify_whatsapp_enabled: form.value.slot_booking_notify_whatsapp_enabled,
      slot_booking_notify_email_enabled: form.value.slot_booking_notify_email_enabled,
      slot_booking_confirmation_message: form.value.slot_booking_confirmation_message,
      // 2026-09-07: owned here now, not Settings -- see hydrate()'s own
      // comment on why this moved.
      capture_datetime_field: form.value.capture_datetime_field,
      capture_datetime_time_field: form.value.capture_datetime_time_field,
      capture_datetime_prompt: form.value.capture_datetime_prompt,
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save slot booking settings'))
      return
    }
    emit('saved', getApiMessage(response, 'Slot booking settings saved.'))
  } catch (err) {
    emit('error', err)
  } finally {
    saving.value = false
  }
}

// -- Resources ---------------------------------------------------------
async function loadSlotResources() {
  if (!taskId.value) {
    slotResources.value = []
    return
  }
  slotResourcesLoading.value = true
  try {
    const response = await fetchSlotResources({ task_id: taskId.value })
    slotResources.value = isApiSuccess(response) && Array.isArray(response.data) ? response.data : []
  } finally {
    slotResourcesLoading.value = false
  }
}

function openAddResource() {
  activeResourceForm.value = 'new'
}
function openEditResource(resource) {
  activeResourceForm.value = resource
}
function closeResourceForm() {
  activeResourceForm.value = null
}
function isEditingResource(resource) {
  return Boolean(
    activeResourceForm.value &&
    activeResourceForm.value !== 'new' &&
    activeResourceForm.value.resource_id === resource.resource_id,
  )
}
function onResourceSaved(message) {
  emit('saved', message)
  activeResourceForm.value = null
  void loadSlotResources()
}
function onResourceError(error) {
  emit('error', getApiMessage(error, 'Failed to save resource'))
}
async function confirmDeleteResource(resource) {
  const resourceId = String(resource?.resource_id ?? '').trim()
  if (!resourceId) return
  pendingDeleteId.value = ''
  deletingResourceId.value = resourceId
  try {
    const response = await deleteSlotResource({ resource_id: resourceId })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to remove resource'))
      return
    }
    emit('saved', 'Resource removed.')
    void loadSlotResources()
  } finally {
    deletingResourceId.value = ''
  }
}

/** e.g. "Sun closed · Mon 09:00-17:00 · ..." -- Sun through Sat, for the compact list row. */
/** e.g. "Mon 09:00-13:00,15:00-18:00 · Wed 09:00-17:00" -- open days only,
 *  closed days omitted, so this actually scans instead of burying the two
 *  open days in five "closed" entries. */
function resourceHoursSummary(resource) {
  const days = [
    ['hours_sun', 'Sun'], ['hours_mon', 'Mon'], ['hours_tue', 'Tue'], ['hours_wed', 'Wed'],
    ['hours_thu', 'Thu'], ['hours_fri', 'Fri'], ['hours_sat', 'Sat'],
  ]
  const open = days
    .map(([field, label]) => [label, String(resource?.[field] ?? '').trim()])
    .filter(([, hours]) => hours)
  if (!open.length) return 'Closed every day'
  return open.map(([label, hours]) => `${label} ${hours}`).join(' · ')
}
</script>

<style scoped>
/*
  Deliberately no max-width on the body -- this used to inherit a shared
  40rem cap meant for narrow settings forms, which wasted most of a wide
  screen ("why can't you take the whole width" -- Ramu, 2026-09-04). The
  Resources section now genuinely uses that space (a card grid instead of
  one narrow stacked column); .tw-slot-narrow opts individual sections
  BACK into a readable form width where full-bleed would just look empty
  (the toggle, the confirmation-message textarea).
*/
.tw-slot-body {
  max-width: none;
}
.tw-slot-narrow {
  max-width: 40rem;
}
.tw-slot-section {
  padding: 1rem 0;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
}
.tw-slot-section:first-child {
  border-top: none;
  padding-top: 0;
}
.tw-slot-section__title {
  font-size: 0.85rem;
  font-weight: 650;
  display: block;
  margin-bottom: 6px;
}
.tw-field-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--y-text-muted, #64748b);
}
.tw-field-hint--warning {
  color: var(--y-color-warning, #b45309);
}
.tw-field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 4px;
}
.tw-textarea {
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: transparent;
  padding: 8px 10px;
  font: inherit;
  color: inherit;
  resize: vertical;
}
.tw-adv-processing {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(30, 77, 255, 0.06);
  border: 1px solid rgba(30, 77, 255, 0.14);
  font-size: 0.85rem;
}
.tw-slot-resources__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}
.tw-slot-resources__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 10px;
  margin-top: 10px;
  align-items: start;
}
/* A resource being edited needs the day-by-day builder's full room, not
   just one grid column's worth -- span the whole row while its panel is open. */
.tw-slot-resources__item--editing {
  grid-column: 1 / -1;
}
.tw-slot-resources__row {
  height: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
}
.tw-slot-resources__row-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.tw-slot-resources__name {
  font-weight: 650;
  font-size: 0.88rem;
}
.tw-slot-resources__meta {
  font-size: 0.78rem;
  color: var(--y-text-muted, #64748b);
}
.tw-slot-resources__hours {
  margin: 4px 0 0;
  font-size: 0.76rem;
  color: var(--y-text-muted, #94a3b8);
}
.tw-slot-resources__actions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}
.tw-slot-notify-channel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 260px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 4px 0;
}
.tw-badge--muted {
  background: rgba(148, 163, 184, 0.18);
  color: var(--y-text-muted);
}
</style>
